<template>
  <div class="mt-4 mb-10 flex flex-col">
    <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div class="flex flex-wrap items-end gap-3">
        <div>
          <label class="block text-xxs uppercase text-gray-400 mb-1">Start Date</label>
          <input
            v-model="startDate"
            type="date"
            class="form-input bg-gray-800 text-sm border-gray-700 w-40"
          />
        </div>
        <div>
          <label class="block text-xxs uppercase text-gray-400 mb-1">End Date</label>
          <input
            v-model="endDate"
            type="date"
            class="form-input bg-gray-800 text-sm border-gray-700 w-40"
          />
        </div>
        <button
          class="btn btn-blue w-auto h-10 px-6 text-center min-w-40"
          @click="fetchReport"
          :disabled="loading"
        >
          {{ loading ? "Loading..." : "Apply Range" }}
        </button>
        <button class="btn btn-green w-auto h-10 px-6 text-center min-w-32" @click="downloadCsv">
          Export CSV
        </button>
      </div>
      <div class="text-sm text-gray-400">
        {{ rows.length }} alert{{ rows.length === 1 ? "" : "s" }}
      </div>
    </div>

    <h3 class="text-lg text-center my-4 font-bold leading-6 text-white">Summary by Bucket</h3>
    <div class="py-2 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <div class="align-middle inline-block min-w-full">
        <table class="min-w-full">
          <thead>
            <tr>
              <th class="sticky top-0 text-left">Bucket</th>
              <th class="sticky top-0 text-right">Alerts</th>
              <th class="sticky top-0 text-right">Avg Peak Return 48h</th>
              <th class="sticky top-0 text-right">Max Winner 48h</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="summary in summaries"
              :key="summary.bucket_label"
              :class="summary.bucket_label === 'Total' ? 'font-bold border-t border-gray-700' : ''"
            >
              <td class="text-left">{{ summary.bucket_label }}</td>
              <td class="text-right">{{ summary.alert_count }}</td>
              <td class="text-right">{{ formatPercent(summary.average_peak_return_48h) }}</td>
              <td class="text-right">{{ formatPercent(summary.max_winner_48h) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <h3 class="text-lg text-center my-4 font-bold leading-6 text-white">Alert Log Rows</h3>
    <div class="py-2 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <div class="align-middle inline-block min-w-full">
        <table class="min-w-full">
          <thead>
            <tr>
              <th class="sticky top-0 text-left">Coin</th>
              <th class="sticky top-0 text-right">Score</th>
              <th class="sticky top-0 text-left">Bucket Label</th>
              <th class="sticky top-0 text-right">Alerted At</th>
              <th class="sticky top-0 text-right">Price at Alert</th>
              <th class="sticky top-0 text-right">Price 24h After</th>
              <th class="sticky top-0 text-right">Price 48h After</th>
              <th class="sticky top-0 text-right">Max Price 48h</th>
              <th class="sticky top-0 text-right">Min Price 48h</th>
              <th class="sticky top-0 text-right">Minutes to Max 48h</th>
              <th class="sticky top-0 text-right">Minutes to Min 48h</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td class="text-left">{{ row.coin }}</td>
              <td class="text-right">{{ formatNumber(row.score, 2, 2) }}</td>
              <td class="text-left">{{ row.bucket_label }}</td>
              <td class="text-right">{{ formatDateTime(row.alerted_at) }}</td>
              <td class="text-right">{{ formatNumber(row.price_usd_at_alert, 2, 8) }}</td>
              <td class="text-right">{{ formatNumber(row.price_usd_24h_after, 2, 8) }}</td>
              <td class="text-right">{{ formatNumber(row.price_usd_48h_after, 2, 8) }}</td>
              <td class="text-right">{{ formatNumber(row.max_price_usd_48h, 2, 8) }}</td>
              <td class="text-right">{{ formatNumber(row.min_price_usd_48h, 2, 8) }}</td>
              <td class="text-right">{{ formatNumber(row.minutes_to_max_price_48h, 0, 2) }}</td>
              <td class="text-right">{{ formatNumber(row.minutes_to_min_price_48h, 0, 2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  props: ["initialPayload"],
  data() {
    return {
      loading: false,
      startDate: this.initialPayload.filters.start_date,
      endDate: this.initialPayload.filters.end_date,
      rows: this.initialPayload.rows,
      summaries: this.initialPayload.summaries,
    };
  },
  methods: {
    fetchReport() {
      this.loading = true;
      axios
        .get("/admin/slack_alert_logs", {
          params: {
            start_date: this.startDate,
            end_date: this.endDate,
          },
        })
        .then((response) => {
          this.rows = response.data.rows;
          this.summaries = response.data.summaries;
          this.startDate = response.data.filters.start_date;
          this.endDate = response.data.filters.end_date;
        })
        .catch((error) => alert(error.message))
        .finally(() => {
          this.loading = false;
        });
    },
    downloadCsv() {
      const searchParams = new URLSearchParams({
        start_date: this.startDate,
        end_date: this.endDate,
      });
      window.location.assign(`/admin/slack_alert_logs.csv?${searchParams.toString()}`);
    },
    formatNumber(value, minimumFractionDigits, maximumFractionDigits) {
      if (value === null || value === undefined) {
        return "-";
      }
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits,
        maximumFractionDigits,
      }).format(value);
    },
    formatPercent(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      const rounded = Number(value).toFixed(2);
      const prefix = rounded > 0 ? "+" : "";
      return `${prefix}${rounded}%`;
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }
      return moment.utc(value).format("YYYY-MM-DD HH:mm");
    },
  },
};
</script>
